import { useEffect, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MediaCard from "@/components/feature/MediaCard";
import EmptyState from "@/components/feature/EmptyState";
import { getHot, searchMedia } from "@/lib/media";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("keyword") ?? "";
  const [input, setInput] = useState(keyword);

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  const results = searchMedia(keyword);
  const hot = getHot(5);

  useDocumentMeta(
    keyword ? `“${keyword}”的搜索结果 - 免费播放片大片` : "影视搜索 - 免费播放片大片",
    keyword
      ? `免费播放片大片为你找到与“${keyword}”相关的影视内容，共 ${results.length} 部，覆盖电影与电视剧资源。`
      : "在免费播放片大片搜索你想看的电影与电视剧，快速找到并了解感兴趣的影视作品。",
    "影视搜索,免费播放片大片,搜索电影,搜索电视剧",
  );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const kw = input.trim();
    setSearchParams(kw ? { keyword: kw } : {});
  };

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-8 md:py-12">
      <nav aria-label="面包屑" className="mb-4 flex items-center gap-1.5 text-sm text-foreground-400">
        <Link to="/" className="transition-colors hover:text-accent-400">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-200">搜索</span>
      </nav>

      <header className="mb-6">
        <h1 className="font-heading text-2xl font-bold text-foreground-50 md:text-4xl">
          影视搜索
        </h1>
        <p className="mt-2 text-sm text-foreground-400">基于片名的全文搜索，覆盖全站电影与电视剧内容</p>
      </header>

      <form onSubmit={submit} className="mb-8 flex flex-col gap-3 sm:flex-row" role="search">
        <div className="flex flex-1 items-center rounded-md border border-background-700 bg-background-900/70 px-4 py-3 focus-within:border-primary-500">
          <i className="ri-search-line text-base text-foreground-400"></i>
          <input
            type="search"
            name="keyword"
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="输入影视片名，例如：深海回声"
            aria-label="搜索影视片名"
            className="w-full bg-transparent px-3 text-sm text-foreground-100 placeholder:text-foreground-500 outline-none"
          />
        </div>
        <button
          type="submit"
          className="flex items-center justify-center gap-2 rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-950 whitespace-nowrap transition-colors hover:bg-accent-400"
        >
          <i className="ri-search-line"></i>
          搜索
        </button>
      </form>

      {keyword ? (
        <p className="mb-5 text-sm text-foreground-400">
          关键词“<span className="font-semibold text-accent-400">{keyword}</span>”共找到{" "}
          <span className="font-semibold text-accent-400">{results.length}</span> 个结果
        </p>
      ) : (
        <p className="mb-5 text-sm text-foreground-400">输入关键词开始搜索，或看看下面的热门推荐。</p>
      )}

      {keyword && results.length === 0 ? (
        <EmptyState
          icon="ri-search-eye-line"
          title="未找到相关影视内容"
          description={`没有找到与“${keyword}”匹配的影视。换一个关键词试试，或返回首页浏览热门影视。`}
          action={
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/"
                className="rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-950 whitespace-nowrap transition-colors hover:bg-accent-400"
              >
                返回首页
              </Link>
              <Link
                to="/movie"
                className="rounded-md border border-background-600 px-5 py-2.5 text-sm font-medium text-foreground-100 whitespace-nowrap transition-colors hover:border-accent-400 hover:text-accent-400"
              >
                浏览电影频道
              </Link>
            </div>
          }
        />
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4 xl:grid-cols-5">
          {(keyword ? results : hot).map((item) => (
            <MediaCard key={item.id} item={item} />
          ))}
        </div>
      )}

      {!keyword && (
        <p className="mt-6 text-sm text-foreground-500">
          <i className="ri-fire-line mr-1 text-accent-400"></i>
          以上为当前热门影视推荐
        </p>
      )}
    </div>
  );
}